import type { FieldType } from "../../types";
import { type BaseIssue, check, type ErrorMessage, instance, pipe, rawCheck, union } from "valibot";
import FieldService from "../../services/FieldService";
import { isNil } from "lodash-es";
import { errorTranslation } from "./base";
import FieldValueReference from "../../fields/FieldValueReference";
import { toField } from "../../fields/fields";
import VariableService from "../../services/VariableService";
import VariableValueReference from "../../variables/VariableValueReference";
import type { VariableType } from "../../variables/types";
import { fieldReference } from "./idReferences";
import MetaVariableReference from "../../metaVariables/MetaVariableReference";
import type { MetaVariableId } from "../../metaVariables/metaVariable";
import { keyFromSelector } from "i18next";

export const fieldReferenceOfTypes = (types: FieldType[]) =>
  pipe(
    fieldReference(),
    rawCheck(({ dataset, addIssue }) => {
      if (dataset.typed) {
        const field = FieldService.getField(dataset.value);
        if (isNil(field)) {
          return addIssue({
            message: errorTranslation(keyFromSelector(($) => $.VALIDATION_FIELD_REFERENCE_DOES_NOT_EXIST)),
          });
        }

        if (!types.includes(field.type)) {
          const fieldType = toField(field.type);
          return addIssue({
            message: errorTranslation(
              keyFromSelector(($) => $.VALIDATION_FIELD_REFERENCE_NOT_ALLOWED),
              {
                displayLabel: fieldType.displayLabel(field.properties),
              },
            ),
          });
        }
      }
    }),
  );

export const fieldValueReferenceOfTypes = (
  types: FieldType[],
  options?: Partial<Record<FieldType, Record<string, unknown>>>,
) =>
  pipe(
    fieldValueReference(),
    rawCheck(({ dataset, addIssue }) => {
      if (dataset.typed) {
        const field = FieldService.getField(dataset.value.id);
        if (isNil(field)) {
          return addIssue({
            message: errorTranslation(keyFromSelector(($) => $.VALIDATION_FIELD_REFERENCE_DOES_NOT_EXIST)),
          });
        }

        const fieldType = toField(field.type);
        const valueReferenceOption = fieldType
          .valueReferenceOptions(field.uid, field.properties, options)
          .find((o) => dataset.value.equals(o.value as FieldValueReference));
        const fieldValueLabel = isNil(valueReferenceOption)
          ? fieldType.displayLabel(field.properties)
          : valueReferenceOption.label;

        if (!types.includes(field.type)) {
          return addIssue({
            message: errorTranslation(
              keyFromSelector(($) => $.VALIDATION_FIELD_REFERENCE_NOT_ALLOWED),
              {
                displayLabel: fieldValueLabel,
              },
            ),
          });
        }

        if (isNil(valueReferenceOption) || valueReferenceOption.isSelectable === false) {
          return addIssue({
            message: errorTranslation(
              keyFromSelector(($) => $.VALIDATION_FIELD_REFERENCE_VALUE_NOT_ALLOWED),
              {
                displayLabel: fieldValueLabel,
              },
            ),
          });
        }
      }
    }),
  );

export const valueReferenceOfTypes = (fieldTypes: FieldType[], variableTypes: VariableType[]) =>
  union(
    [fieldValueReferenceOfTypes(fieldTypes), variableValueReferenceOfTypes(variableTypes)],
    errorTranslation(keyFromSelector(($) => $.VALIDATION_NO_SELECTION)),
  );

export const variableValueReferenceOfTypes = (types: VariableType[], message?: ErrorMessage<BaseIssue<unknown>>) =>
  pipe(
    variableValueReference(message),
    rawCheck(({ dataset, addIssue }) => {
      if (dataset.typed) {
        const variable = VariableService.getVariable(dataset.value.id);
        if (isNil(variable)) {
          return addIssue({
            message: message ?? errorTranslation(keyFromSelector(($) => $.VALIDATION_FIELD_REFERENCE_DOES_NOT_EXIST)),
          });
        }

        if (!types.includes(variable.type)) {
          return addIssue({
            message:
              message ??
              errorTranslation(
                keyFromSelector(($) => $.VALIDATION_FIELD_REFERENCE_NOT_ALLOWED),
                {
                  displayLabel: variable.name,
                },
              ),
          });
        }
      }
    }),
  );

export const metaVariableValueReferenceOfTypes = (
  types: MetaVariableId[],
  message?: ErrorMessage<BaseIssue<unknown>>,
) =>
  pipe(
    metaVariableValueReference(message),
    check(
      (value) => types.includes(value.id),
      message ?? errorTranslation(keyFromSelector(($) => $.VALIDATION_FIELD_REFERENCE_DOES_NOT_EXIST)),
    ),
  );

const fieldValueReference = (message?: ErrorMessage<BaseIssue<unknown>>) =>
  instance(FieldValueReference, message ?? errorTranslation(keyFromSelector(($) => $.VALIDATION_NO_SELECTION)));

const variableValueReference = (message?: ErrorMessage<BaseIssue<unknown>>) =>
  instance(VariableValueReference, message ?? errorTranslation(keyFromSelector(($) => $.VALIDATION_NO_SELECTION)));

const metaVariableValueReference = (message?: ErrorMessage<BaseIssue<unknown>>) =>
  instance(MetaVariableReference, message ?? errorTranslation(keyFromSelector(($) => $.VALIDATION_NO_SELECTION)));
